import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { requestWithAuthRecovery } from "@/lib/authRequest";

export type UserRole =
  | "trial"
  | "expired"
  | "pro"
  | "business"
  | "admin"
  | "membership"
  // legacy — kept so leftover rows don't crash typing during migration
  | "demo"
  | "standard";

interface UseUserRoleReturn {
  role: UserRole | null;
  loading: boolean;
  isTrial: boolean;
  isExpired: boolean;
  isPro: boolean;
  isBusiness: boolean;
  isAdmin: boolean;
  canWrite: boolean;
  canAccessPersonal: boolean;
  canAccessGroups: boolean;
  canAccessCalendarSync: boolean;
  canAccessTelegram: boolean;
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  // legacy alias, kept for components not yet migrated
  isDemo: boolean;
}

interface RoleSnapshot {
  role: UserRole;
  trialEndsAt: string | null;
}

const roleSnapshots = new Map<string, RoleSnapshot>();
const roleRequests = new Map<string, Promise<RoleSnapshot | null>>();

const loadRoleSnapshot = (userId: string) => {
  const cached = roleSnapshots.get(userId);
  if (cached) return Promise.resolve(cached);

  const pending = roleRequests.get(userId);
  if (pending) return pending;

  const request = (async () => {
    const { data, error } = await requestWithAuthRecovery(() =>
      supabase.from("user_roles").select("role").eq("user_id", userId).single(),
    );
    if (error || !data) return null;

    const { data: profile } = await requestWithAuthRecovery(() =>
      supabase.from("profiles").select("trial_ends_at").eq("user_id", userId).maybeSingle(),
    );
    const snapshot: RoleSnapshot = {
      role: data.role as UserRole,
      trialEndsAt: (profile as { trial_ends_at?: string | null } | null)?.trial_ends_at ?? null,
    };
    roleSnapshots.set(userId, snapshot);
    localStorage.setItem("user_role", snapshot.role);
    return snapshot;
  })().finally(() => roleRequests.delete(userId));

  roleRequests.set(userId, request);
  return request;
};

export const useUserRole = (): UseUserRoleReturn => {
  const { user, initializing } = useAuth();
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (initializing) return;

    const fetchUserRole = async () => {
      if (!isMounted) return;

      try {
        setLoading(true);

        if (!user) {
          // Use cached role if available, otherwise set to null
          const cachedRole = localStorage.getItem('user_role');
          if (cachedRole) {
            setRole(cachedRole as UserRole);
          } else {
            setRole(null);
          }
          setLoading(false);
          return;
        }

        const snapshot = await loadRoleSnapshot(user.id);

        if (!isMounted) return;

        if (!snapshot) {
          // Use cached role as fallback
          const cachedRole = localStorage.getItem('user_role');
          if (cachedRole) {
            setRole(cachedRole as UserRole);
          } else {
            console.warn("Could not fetch user role");
            setRole(null);
          }
        } else {
          setRole(snapshot.role);
          setTrialEndsAt(snapshot.trialEndsAt);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error in fetchUserRole:", error);
          const cachedRole = localStorage.getItem('user_role');
          if (cachedRole) {
            setRole(cachedRole as UserRole);
          } else {
            setRole(null);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserRole();

    return () => {
      isMounted = false;
    };
    // Re-fetch only when the signed-in identity actually changes, never on
    // routine token refreshes.
  }, [user?.id, initializing]);

  // Treat legacy "demo" rows (should be migrated already) as trial for safety
  const isTrial = role === "trial" || role === "demo";
  const isExpired = role === "expired" || role === "standard";
  const isPro = role === "pro";
  const isBusiness = role === "business";
  const isAdmin = role === "admin";
  const isMembership = role === "membership";

  // New tier model:
  //   Trial      → full Pro access (7 days)
  //   Pro        → personal + all integrations, no groups
  //   Business   → everything, including group collaboration
  //   Membership → complimentary Business-equivalent
  //   Admin      → full access
  //   Expired    → read-only, must resubscribe
  const canWrite = !isExpired && role !== null;
  const canAccessPersonal = canWrite;
  const canAccessGroups = isBusiness || isAdmin || isMembership;
  const canAccessCalendarSync = isTrial || isPro || isBusiness || isAdmin || isMembership;
  const canAccessTelegram = isTrial || isPro || isBusiness || isAdmin || isMembership;

  const trialDaysRemaining = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;

  return {
    role,
    loading,
    isTrial,
    isExpired,
    isPro,
    isBusiness,
    isAdmin,
    canWrite,
    canAccessPersonal,
    canAccessGroups,
    canAccessCalendarSync,
    canAccessTelegram,
    trialEndsAt,
    trialDaysRemaining,
    // legacy alias, kept so old callers compile.
    // The old "demo" tier no longer exists; trial users have full Pro
    // access, so returning false makes every legacy `isDemo` gate no-op,
    // which is the correct behaviour under the new model.
    isDemo: false,
  };
};
